import React from "react";
import { Icon } from "@iconify/react";
import EmailPreview from "../../components/LaunchedTemplates/EmailPreview";
import ToggleButton from "../../components/ToggleButton/ToggleButton";

const notificationTypeLabel = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

// Renders a single launched-automation record.
// Uses the same visual language as Notification's ScheduledEventCard
// (badges, launch-info strip). The launched-automations schema currently
// returns only templateCode/templateStatus references, but if the backend
// starts including `cronJobTemplate` (same shape as the /save payload:
// { body, htmlBody, subject, ... }) this renders it via the shared,
// HTML-aware EmailPreview automatically — no changes needed here.
const ScheduledCronJobCard = ({
  job,
  onReview,
  onDownload,
  // Shown only for templates already in use; sends the person to the copy
  // they downloaded earlier (Main tab).
  onViewUsed,
  // Main tab: marks the template the person was sent here to see.
  highlighted = false,
  isDownloading,
  displayMode = "gallery",
  reviewLabel = "Customize & Download",
  reviewIconOnly = false,
  onToggleStatus,
  isTogglingStatus = false,
  onDelete,
  isDeleting = false,
}) => {
  const hasLaunchInfo =
    job.launchTemplateStatus != null ||
    job.launchDate != null ||
    job.launchTemplateVersionType != null;

  // Prefer the API's rendered template: it shows the real recipient values
  // rather than the editable {Placeholder} source template.
  const template = job.cronJobTemplatePreview ?? job.cronJobTemplate ?? {};

  return (
    <div
      className={`scheduled-event-card scheduled-event-card--${displayMode}${highlighted ? " scheduled-event-card--highlighted" : ""}`}
    >
      <div className="scheduled-event-card__header">
        <span className="scheduled-event-card__task">{job.taskName}</span>
        <div className="scheduled-event-card__status-actions">
          {onToggleStatus && (
            <ToggleButton
              isOn={String(job.status).toUpperCase() === "ENABLED"}
              onToggle={() => !isTogglingStatus && onToggleStatus(job)}
            />
          )}
          {onReview && reviewIconOnly && (
            <button
              type="button"
              className="scheduled-event-card__review-button"
              onClick={() => onReview(job)}
              disabled={isDownloading}
              aria-label="Edit downloaded template"
              title="Edit"
            >
              <Icon icon="mdi:pencil-outline" />
            </button>
          )}
          {onDelete && reviewIconOnly && (
            <button
              type="button"
              className="scheduled-event-card__review-button scheduled-event-card__review-button--delete"
              onClick={() => onDelete(job)}
              disabled={isDeleting}
              aria-label="Delete downloaded template"
              title="Delete"
            >
              <Icon icon={isDeleting ? "mdi:loading" : "mdi:delete-outline"} />
            </button>
          )}
        </div>
      </div>

      {job.notificationTypes?.length > 0 && (
        <div className="notification-type-pills">
          {job.notificationTypes.map((notificationType) => (
            <span
              key={notificationType.notificationTypeId}
              className="notification-type-pill"
            >
              <Icon
                icon={
                  notificationType.notificationType === "EMAIL"
                    ? "mdi:email-outline"
                    : "mdi:bell-outline"
                }
              />
              {notificationTypeLabel(notificationType.notificationType)}
            </span>
          ))}
        </div>
      )}

      {hasLaunchInfo && (
        <div className="launch-info-strip">
          {job.launchTemplateStatus && (
            <span
              className={`badge badge--${String(job.launchTemplateStatus).toLowerCase().replace(/\s+/g, "_")}`}
            >
              {job.launchTemplateStatus}
            </span>
          )}
          {job.launchTemplateVersionType && (
            <span className="launch-info-strip__version">
              {job.launchTemplateVersionType}
            </span>
          )}
          {job.launchDate && (
            <span className="launch-info-strip__date">
              <Icon icon="mdi:calendar-clock-outline" />
              {new Date(job.launchDate).toLocaleDateString()}
            </span>
          )}
          {job.alreadyUsed != null && (
            <span
              className={`launch-info-strip__used ${
                job.alreadyUsed ? "launch-info-strip__used--yes" : ""
              }`}
            >
              <Icon
                icon={
                  job.alreadyUsed
                    ? "mdi:check-circle-outline"
                    : "mdi:circle-outline"
                }
              />
              {job.alreadyUsed
                ? Number(job.noOfTimeUsed) > 0
                  ? `Used · ${job.noOfTimeUsed} ${Number(job.noOfTimeUsed) === 1 ? "time" : "times"}`
                  : "Used"
                : "Unused"}
            </span>
          )}
        </div>
      )}

      {template.body && (
        <EmailPreview
          body={template.body}
          htmlBody={template.htmlBody}
          label={template.subject || "Email Preview"}
          variant="marketplace"
        />
      )}

      {(onDownload || onReview) && (
        <div className="marketplace-card-actions">
          {onDownload && (
            <button
              type="button"
              className="marketplace-card-actions__download"
              onClick={() => onDownload(job)}
              disabled={isDownloading}
            >
              <Icon
                icon={isDownloading ? "mdi:loading" : "mdi:download-outline"}
              />
              {isDownloading ? "Downloading..." : "Download"}
            </button>
          )}
          {onViewUsed && job.alreadyUsed && (
            <button
              type="button"
              className="marketplace-card-actions__view-used"
              onClick={() => onViewUsed(job)}
              disabled={isDownloading}
            >
              <Icon icon="mdi:open-in-new" />
              View used template
            </button>
          )}
          {onReview && !reviewIconOnly && (
            <button
              type="button"
              className="marketplace-card-actions__edit"
              onClick={() => onReview(job)}
              disabled={isDownloading}
            >
              <Icon icon="mdi:tune-variant" />
              {reviewLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ScheduledCronJobCard;
